import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2, Circle, ChevronUp, ChevronDown } from 'lucide-react';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { useToast } from '@shared/hooks/useToast';

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="ggj-param-group overflow-hidden">
      <div className="ggj-profile-section-h">{title}</div>
      <div className="ggj-profile-section-body">{children}</div>
    </div>
  );
}
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { orgService } from '@core/supabase/services/orgService';
import { supabase } from '@core/supabase/client';
import { invalidateOpenProfilesCache } from '@core/auth/rbac';
import { Organization, ApplicationStatus } from '@core/types';
import {
  ALL_STATUSES,
  PIPELINE_STATUS_LABEL,
  parseOrgPipelineOrder,
} from '@core/constants/pipelineStages';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { ContextAiAssistModal } from '@shared/components/ai/ContextAiAssistModal';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

export function OrgSettingsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    name: '', billing_email: '', gst_number: '',
  });

  const [compliance, setCompliance] = useState({
    retention_days: 730,
    consent_version: '',
    gdpr_dpa_signed: false,
  });
  const [savingCompliance, setSavingCompliance] = useState(false);
  const [savedCompliance, setSavedCompliance] = useState(false);
  const [complianceAiOpen, setComplianceAiOpen] = useState(false);

  /* Destructive action confirm dialogs */
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { toast, showToast } = useToast();

  const complianceAiContext = useMemo(
    () =>
      `retention_days: ${compliance.retention_days}\nconsent_version: ${compliance.consent_version || '—'}\ngdpr_dpa_signed: ${compliance.gdpr_dpa_signed}\norg: ${org?.name ?? '—'}`,
    [compliance.retention_days, compliance.consent_version, compliance.gdpr_dpa_signed, org?.name]
  );

  interface OnboardingStep { id: string; label: string; detail: string; href: string; done: boolean }
  const [onboarding, setOnboarding] = useState<OnboardingStep[]>([]);

  const [pipelineOrder, setPipelineOrder] = useState<ApplicationStatus[]>(() => [...ALL_STATUSES]);
  const [savingPipeline, setSavingPipeline] = useState(false);
  const [savedPipeline, setSavedPipeline] = useState(false);

  // Phase 8 · C-47 — `settings.open_profiles` lets peers view each other's
  // profile pages at /team/:orgUserId. Off by default.
  const [openProfiles, setOpenProfiles] = useState(false);
  const [savingOpenProfiles, setSavingOpenProfiles] = useState(false);
  const [savedOpenProfiles, setSavedOpenProfiles] = useState(false);

  useEffect(() => {
    if (!user?.orgId) return;
    const orgId = user.orgId;

    orgService.getOrg(orgId).then((o) => {
      setOrg(o);
      if (o) {
        setForm({ name: o.name, billing_email: o.billing_email ?? '', gst_number: o.gst_number ?? '' });
        setCompliance({
          retention_days: o.retention_days ?? 730,
          consent_version: o.consent_version ?? '',
          gdpr_dpa_signed: Boolean(o.gdpr_dpa_signed),
        });
        setPipelineOrder(parseOrgPipelineOrder(o.pipeline_stage_order) ?? [...ALL_STATUSES]);
        setOpenProfiles(
          typeof o.settings === 'object' &&
            o.settings !== null &&
            (o.settings as Record<string, unknown>)['open_profiles'] === true,
        );
      }
    }).finally(() => setLoading(false));

    // PUB-020: derive onboarding checklist from real data
    Promise.all([
      supabase.from('ggj_b2b_org_users').select('id', { count: 'exact', head: true }).eq('org_id', orgId),
      supabase.from('ggj_b2b_clients').select('id', { count: 'exact', head: true }).eq('org_id', orgId),
      supabase.from('ggj_b2b_jobs').select('id', { count: 'exact', head: true }).eq('org_id', orgId),
      supabase.from('ggj_b2b_candidates').select('id', { count: 'exact', head: true }).eq('org_id', orgId),
      supabase.from('ggj_b2b_org_integrations').select('id', { count: 'exact', head: true })
        .eq('org_id', orgId).eq('provider', 'smtp'),
    ]).then(([users, clients, jobs, candidates, smtp]) => {
      setOnboarding([
        {
          id: 'org_name',
          label: 'Set your organisation name',
          detail: 'Add your consultancy name in org settings.',
          href: '/settings/organisation',
          done: true,
        },
        {
          id: 'team_member',
          label: 'Invite a team member',
          detail: 'Go to the Team page and invite at least one colleague.',
          href: '/team',
          done: (users.count ?? 0) > 1,
        },
        {
          id: 'first_client',
          label: 'Add your first client',
          detail: 'Create a client in the Clients module.',
          href: '/clients',
          done: (clients.count ?? 0) > 0,
        },
        {
          id: 'first_job',
          label: 'Post your first job',
          detail: 'Create an open job linked to a client.',
          href: '/jobs/new',
          done: (jobs.count ?? 0) > 0,
        },
        {
          id: 'first_candidate',
          label: 'Add your first candidate',
          detail: 'Import or create a candidate record.',
          href: '/candidates',
          done: (candidates.count ?? 0) > 0,
        },
        {
          id: 'smtp',
          label: 'Configure SMTP for outbound email',
          detail: 'Set up your sender domain in Settings → Integrations.',
          href: '/settings/integrations',
          done: (smtp.count ?? 0) > 0,
        },
      ]);
    });
  }, [user?.orgId]);

  const handleSave = async () => {
    if (!user?.orgId || !perms.canManageTeam) return;
    setSaving(true);
    try {
      await orgService.updateOrg(user.orgId, {
        name: form.name,
        billing_email: form.billing_email || null,
        gst_number: form.gst_number || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      showToast({ title: 'Organisation saved', body: 'Profile details have been updated.' });
    } catch (err) {
      showToast({ title: 'Save failed', body: err instanceof Error ? err.message : 'Could not save changes.', kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePipelineSave = async () => {
    if (!user?.orgId || !perms.canManageTeam) return;
    setSavingPipeline(true);
    setSavedPipeline(false);
    try {
      await orgService.updateOrg(user.orgId, { pipeline_stage_order: pipelineOrder });
      setSavedPipeline(true);
      setTimeout(() => setSavedPipeline(false), 3000);
      showToast({ title: 'Pipeline order saved' });
    } catch (err) {
      showToast({ title: 'Save failed', body: err instanceof Error ? err.message : 'Could not save pipeline order.', kind: 'error' });
    } finally {
      setSavingPipeline(false);
    }
  };

  const handleOpenProfilesToggle = async (next: boolean) => {
    if (!user?.orgId || !perms.canManageTeam) return;
    const orgId = user.orgId;
    const previous = openProfiles;
    setOpenProfiles(next);
    setSavingOpenProfiles(true);
    setSavedOpenProfiles(false);
    try {
      const currentSettings =
        (org?.settings && typeof org.settings === 'object' ? org.settings : {}) as Record<
          string,
          unknown
        >;
      const nextSettings = { ...currentSettings, open_profiles: next };
      await orgService.updateOrg(orgId, { settings: nextSettings });
      invalidateOpenProfilesCache(orgId);
      setOrg((prev) => (prev ? { ...prev, settings: nextSettings } : prev));
      setSavedOpenProfiles(true);
      setTimeout(() => setSavedOpenProfiles(false), 3000);
      showToast({
        title: next ? 'Open profiles enabled' : 'Open profiles disabled',
        body: next
          ? 'Team members can now view each other\u2019s profiles at /team.'
          : 'Only admins and managers can view other members\u2019 profiles.',
      });
    } catch (err) {
      setOpenProfiles(previous);
      showToast({
        title: 'Save failed',
        body: err instanceof Error ? err.message : 'Could not update profile visibility.',
        kind: 'error',
      });
    } finally {
      setSavingOpenProfiles(false);
    }
  };

  const handleComplianceSave = async () => {
    if (!user?.orgId || !perms.canManageTeam) return;
    setSavingCompliance(true);
    setSavedCompliance(false);
    try {
      const days = Math.min(3650, Math.max(30, Number(compliance.retention_days) || 730));
      let gdprAt: string | null = null;
      if (compliance.gdpr_dpa_signed) {
        gdprAt =
          org?.gdpr_dpa_signed && org?.gdpr_dpa_signed_at
            ? org.gdpr_dpa_signed_at
            : new Date().toISOString();
      }
      await orgService.updateOrg(user.orgId, {
        retention_days: days,
        consent_version: compliance.consent_version.trim() || null,
        gdpr_dpa_signed: compliance.gdpr_dpa_signed,
        gdpr_dpa_signed_at: gdprAt,
      });
      const refreshed = await orgService.getOrg(user.orgId);
      setOrg(refreshed);
      setSavedCompliance(true);
      setTimeout(() => setSavedCompliance(false), 3000);
      showToast({ title: 'Compliance saved', body: 'Retention and DPA settings updated.' });
    } catch (err) {
      showToast({ title: 'Save failed', body: err instanceof Error ? err.message : 'Could not save compliance settings.', kind: 'error' });
    } finally {
      setSavingCompliance(false);
    }
  };

  const set = (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  if (loading) return <FullPageSpinner />;

  return (
    <div className="space-y-4 ggj-settings-org">
      {/* Toast portal */}
      {toast}
      {/* Phase 8 T-13 §13 B — section header */}
      <div className="mb-1">
        <h2 className="text-[15px] font-semibold">Branding</h2>
        <p className="text-[12px] vscode-text-tertiary mt-0.5">Organisation name, compliance settings and pipeline customisation.</p>
      </div>

      {/* ── Organisation Profile — same grid as Profile → Workspace (bold label | regular value) ── */}
      <Section title="Organisation Profile">
        <dl className="ggj-attr-grid">
          <dt>Consultancy name</dt>
          <dd>
            <input
              className="input w-full ggj-text-body font-normal"
              value={form.name}
              onChange={set('name')}
              disabled={!perms.canManageTeam}
              autoComplete="organization"
            />
          </dd>
          <dt>Billing email</dt>
          <dd>
            <input
              className="input w-full ggj-text-body font-normal"
              type="email"
              value={form.billing_email}
              onChange={set('billing_email')}
              disabled={!perms.canManageTeam}
              autoComplete="email"
            />
          </dd>
          <dt>GST number</dt>
          <dd>
            <input
              className="input w-full ggj-text-body font-normal"
              value={form.gst_number}
              onChange={set('gst_number')}
              disabled={!perms.canManageTeam}
            />
          </dd>
        </dl>
        {perms.canManageTeam && (
          <div className="flex flex-wrap items-center gap-3 pt-3 mt-1 border-t border-[var(--vscode-border-divider)]">
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Saving…</> : 'Save profile'}
            </button>
            {saved && <span className="ggj-text-body font-normal text-green-500">Saved ✓</span>}
          </div>
        )}
      </Section>

      {/* ── Pipeline Configuration ── */}
      {perms.canManageTeam && (
        <Section title="Pipeline Column Order">
          <div className="space-y-3">
            <p className="ggj-text-body font-normal vscode-text-secondary">
              Controls left-to-right Kanban columns on Applications. All stages stay visible; this only reorders them.
            </p>
            <ul className="rounded-[10px] border vscode-border divide-y divide-[var(--vscode-border-primary)] max-h-72 overflow-y-auto">
              {pipelineOrder.map((s, i) => (
                <li key={s} className="flex items-center gap-2 px-3 py-2 ggj-text-body vscode-text-primary">
                  <span className="flex-1 truncate font-semibold text-black dark:text-zinc-200">{PIPELINE_STATUS_LABEL[s]}</span>
                  <span className="shrink-0 ggj-text-body font-normal text-black/70 dark:text-zinc-400 font-mono">{s}</span>
                  <button type="button" className="p-1 rounded hover:vscode-bg-hover vscode-text-tertiary disabled:opacity-30"
                    disabled={i === 0} aria-label="Move up"
                    onClick={() => {
                      if (i === 0) return;
                      setPipelineOrder((prev) => { const n = [...prev]; [n[i-1], n[i]] = [n[i], n[i-1]]; return n; });
                    }}>
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button type="button" className="p-1 rounded hover:vscode-bg-hover vscode-text-tertiary disabled:opacity-30"
                    disabled={i === pipelineOrder.length - 1} aria-label="Move down"
                    onClick={() => {
                      if (i >= pipelineOrder.length - 1) return;
                      setPipelineOrder((prev) => { const n = [...prev]; [n[i], n[i+1]] = [n[i+1], n[i]]; return n; });
                    }}>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="btn-secondary ggj-text-meta" onClick={() => setPipelineOrder([...ALL_STATUSES])}>
                Reset default order
              </button>
              <button type="button" className="btn-primary ggj-text-meta" disabled={savingPipeline} onClick={() => void handlePipelineSave()}>
                {savingPipeline ? 'Saving…' : 'Save column order'}
              </button>
              {savedPipeline && <span className="ggj-text-meta text-green-400">Saved ✓</span>}
            </div>
          </div>
        </Section>
      )}

      {/* ── Team visibility (Phase 8 · C-47) ── */}
      {perms.canManageTeam && (
        <Section title="Team Visibility">
          <div className="space-y-3">
            <p className="ggj-text-body font-normal vscode-text-secondary">
              Controls whether non-admin team members can open each other&rsquo;s profile pages at <code className="font-mono text-[12px] vscode-text-tertiary">/team/:id</code>. Admins and managers always see their own reports regardless of this setting.
            </p>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border vscode-border mt-0.5 shrink-0"
                checked={openProfiles}
                disabled={savingOpenProfiles}
                onChange={(e) => void handleOpenProfilesToggle(e.target.checked)}
              />
              <span className="min-w-0">
                <span className="ggj-text-body font-semibold text-black dark:text-zinc-200 leading-snug block">
                  Allow team members to view each other&rsquo;s profiles
                </span>
                <span className="ggj-text-meta vscode-text-tertiary leading-snug block mt-0.5">
                  When off, peers see an &ldquo;Access denied&rdquo; page for members outside their reporting scope.
                </span>
              </span>
            </label>
            <div className="flex items-center gap-3 text-[12px]">
              {savingOpenProfiles && (
                <span className="inline-flex items-center gap-1.5 vscode-text-tertiary">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving&hellip;
                </span>
              )}
              {savedOpenProfiles && <span className="text-green-500">Saved ✓</span>}
            </div>
          </div>
        </Section>
      )}

      {/* ── Data & Compliance ── */}
      {perms.canManageTeam && (
        <Section title="Data &amp; Compliance">
          <div className="space-y-4">
            <p className="ggj-text-meta font-normal vscode-text-secondary">
              Retention and consent flags apply to candidate data operations. Legal review recommended.
            </p>
            <dl className="ggj-attr-grid">
              <dt>Retention period (days)</dt>
              <dd>
                <input
                  className="input max-w-xs ggj-text-body font-normal"
                  type="number"
                  min={30}
                  max={3650}
                  value={compliance.retention_days}
                  onChange={(e) => setCompliance((c) => ({ ...c, retention_days: parseInt(e.target.value, 10) || 730 }))}
                />
                <p className="ggj-text-meta font-normal vscode-text-tertiary mt-1.5">
                  Horizon before anonymising inactive personal data (30–3650).
                </p>
              </dd>
              <dt>Consent / privacy policy version</dt>
              <dd>
                <input
                  className="input w-full max-w-md ggj-text-body font-normal"
                  placeholder="e.g. v2026-03"
                  value={compliance.consent_version}
                  onChange={(e) => setCompliance((c) => ({ ...c, consent_version: e.target.value }))}
                />
              </dd>
            </dl>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" className="rounded border vscode-border mt-0.5 shrink-0"
                checked={compliance.gdpr_dpa_signed}
                onChange={(e) => setCompliance((c) => ({ ...c, gdpr_dpa_signed: e.target.checked }))} />
              <span className="ggj-text-body font-semibold text-black dark:text-zinc-200 leading-snug">
                GDPR DPA / data processing agreement acknowledged
              </span>
            </label>
            {org?.gdpr_dpa_signed_at && (
              <p className="ggj-text-meta vscode-text-tertiary">
                Recorded: {formatDateTimeGmt(org.gdpr_dpa_signed_at)}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button className="btn-secondary" type="button" onClick={() => void handleComplianceSave()} disabled={savingCompliance}>
                {savingCompliance ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Saving…</> : 'Save compliance'}
              </button>
              <button type="button" className="btn-secondary ggj-text-meta" onClick={() => setComplianceAiOpen(true)}>
                AI Retention Checklist
              </button>
              {savedCompliance && <span className="ggj-text-body text-green-400">Saved ✓</span>}
            </div>
          </div>
        </Section>
      )}

      {/* ── Publishing ── */}
      {perms.canManageTeam && (
        <Section title="Public Job Publishing">
          <p className="ggj-text-meta vscode-text-tertiary leading-relaxed">
            Scheduled go-live times and publish state are shown on each job's overview (Publish status). Review open roles on the{' '}
            <Link to="/jobs" className="text-brand-400 hover:underline">Jobs          </Link> list before sharing your careers link.
          </p>
        </Section>
      )}

      {/* ── Onboarding checklist ── */}
      {onboarding.length > 0 && (() => {
        const doneCount = onboarding.filter((s) => s.done).length;
        const allDone = doneCount === onboarding.length;
        return (
          <Section title={<span className="flex items-center justify-between w-full mr-4">Getting Started<span className="ggj-text-meta vscode-text-tertiary font-normal">{doneCount} / {onboarding.length}</span></span>}>
            <div className="space-y-3">
              <div className="h-1.5 rounded-full vscode-bg-tertiary">
                <div className="h-full rounded-full bg-brand-500 transition-all"
                  style={{ width: `${Math.round((doneCount / onboarding.length) * 100)}%` }} />
              </div>
              {allDone ? (
                <p className="ggj-text-body text-green-400 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Setup complete — you're all set!
                </p>
              ) : (
                <ul className="space-y-2">
                  {onboarding.map((step) => (
                    <li key={step.id} className="flex items-start gap-2.5 ggj-text-body">
                      {step.done
                        ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-green-400" />
                        : <Circle       className="w-4 h-4 shrink-0 mt-0.5 vscode-text-tertiary" />}
                      <div className="min-w-0">
                        <Link to={step.href}
                          className={`font-medium ${step.done ? 'vscode-text-tertiary line-through' : 'vscode-text-primary hover:text-brand-400'}`}>
                          {step.label}
                        </Link>
                        {!step.done && <p className="ggj-text-meta vscode-text-tertiary mt-0.5">{step.detail}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Section>
        );
      })()}

      {/* ── Danger zone — Phase 8 T-13 §13 C ── */}
      {perms.canManageTeam && (
        <Section title="Danger zone">
          <div className="space-y-4">
            <p className="ggj-text-meta vscode-text-secondary leading-relaxed">
              Destructive actions below affect the entire organisation. Export your data first via the{' '}
              <Link to="/settings/offboarding" className="text-brand-400 hover:underline">Tenant Exit Checklist</Link>.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="text-[13px] text-[#EF4444] hover:underline text-left w-fit"
                onClick={() => setConfirmLeave(true)}
              >
                Leave organisation
              </button>
              <button
                type="button"
                className="text-[13px] text-[#EF4444] hover:underline text-left w-fit"
                onClick={() => setConfirmDelete(true)}
              >
                Delete organisation
              </button>
            </div>
          </div>
        </Section>
      )}

      <ConfirmDialog
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={() => { setConfirmLeave(false); /* TODO(§8-v2): call leave org endpoint */ }}
        title="Leave organisation?"
        message="You will lose access to this workspace immediately. Complete the Tenant Exit Checklist first to export your data."
        confirmLabel="Leave"
        variant="danger"
        requireTypedConfirm="DELETE"
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => { setConfirmDelete(false); /* TODO(§8-v2): call delete org endpoint */ }}
        title="Delete organisation?"
        message="This permanently removes the workspace and all its data. This action cannot be undone. Export your data via the Tenant Exit Checklist before proceeding."
        confirmLabel="Delete"
        variant="danger"
        requireTypedConfirm="DELETE"
      />

      <ContextAiAssistModal
        open={complianceAiOpen}
        onClose={() => setComplianceAiOpen(false)}
        orgId={user?.orgId}
        feature="ai_assist_compliance_retention_review"
        preset="compliance_retention_review"
        title="AI — data retention review"
        contextHint={complianceAiContext}
        original=""
        showApplyActions={false}
        onApply={() => {}}
      />
    </div>
  );
}
